import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { authApi, mergeSubscriptionParameters } from '@/api/auth'
import { useAuthStore } from '@/stores/authStore'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/utils/constants'
import { useState, type FormEvent } from 'react'
import {
  TextField,
  Button,
  Alert,
  Box,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Divider,
  Typography,
} from '@mui/material'
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material'
import { DemoQuickLoginBlock } from '@/components/auth/DemoQuickLoginBlock'
import { DEMO_LOGIN_PASSWORD } from '@/components/auth/demoLoginUsers'

const createLoginSchema = (t: (key: string) => string) => z.object({
  email: z
    .string()
    .min(1, t('auth.validation.emailRequired'))
    .email(t('auth.validation.emailInvalid')),
  password: z
    .string()
    .min(8, t('auth.validation.passwordMin')),
  remember_me: z.boolean().optional(),
})

export function LoginForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [demoUserEmail, setDemoUserEmail] = useState('')

  const loginSchema = createLoginSchema(t)
  type LoginFormData = z.infer<typeof loginSchema>

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      remember_me: false,
    },
  })

  const performLogin = async (credentials: { email: string; password: string; remember_me?: boolean }) => {
    try {
      setError(null)
      setIsLoading(true)
      const response = await authApi.login(credentials)
      const user = await mergeSubscriptionParameters(response.user)
      setAuth(user, response.token, {
        expiresIn: response.expires_in,
        rememberMe: credentials.remember_me ?? false,
      })
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage = error.response?.data?.message || error.message || t('auth.invalidCredentials')
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async (data: LoginFormData) => {
    await performLogin({
      email: data.email,
      password: data.password,
      remember_me: data.remember_me,
    })
  }

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (__SPA_DEMO_LOGIN__ && demoUserEmail) {
      void performLogin({ email: demoUserEmail, password: DEMO_LOGIN_PASSWORD })
      return
    }
    void handleSubmit(onSubmit)(e)
  }

  return (
    <Box
      component="form"
      onSubmit={handleFormSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      {__SPA_DEMO_LOGIN__ ? (
        <>
          <DemoQuickLoginBlock
            value={demoUserEmail}
            onChange={setDemoUserEmail}
            disabled={isLoading}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              my: 0.5,
            }}
          >
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('auth.demoOr')}
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
        </>
      ) : null}
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('auth.email')}
            type="email"
            fullWidth
            error={!!errors.email}
            helperText={errors.email?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Controller
        name="password"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('auth.password')}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            error={!!errors.password}
            helperText={errors.password?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Controller
        name="remember_me"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={field.value || false}
                color="primary"
              />
            }
            label={t('auth.rememberMe')}
          />
        )}
      />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={isLoading}
        sx={{
          mt: 1,
          py: 1.5,
        }}
      >
        {isLoading ? t('common.loading') : t('auth.login')}
      </Button>
    </Box>
  )
}
